import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    LabelList
} from 'recharts';
import { FiArrowLeft } from 'react-icons/fi';

const SFRTPaymentAnalytics = () => {
    const [yearlyData, setYearlyData] = useState([]);
    const [monthlyData, setMonthlyData] = useState([]);
    const [paymentList, setPaymentList] = useState([]);
    const [loading, setLoading] = useState({
        yearly: true,
        monthly: false,
        list: false
    });
    const [error, setError] = useState('');
    const [view, setView] = useState('yearly'); // yearly, monthly, list
    const [selectedYear, setSelectedYear] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(null);

    // Fetch yearly payment data
    const fetchYearlyData = async () => {
        try {
            setLoading(prev => ({ ...prev, yearly: true }));
            setError('');
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/sfrtpayments/analytics/yearly`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            if (response.data.success) {
                setYearlyData(response.data.data);
            }
        } catch (err) {
            setError(`Failed to fetch yearly data: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(prev => ({ ...prev, yearly: false }));
        }
    };

    // Fetch monthly data for a specific year
    const fetchMonthlyData = async (year) => {
        try {
            setLoading(prev => ({ ...prev, monthly: true }));
            setError('');
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/sfrtpayments/analytics/monthly/${year}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            if (response.data.success) {
                setMonthlyData(response.data.data);
            }
        } catch (err) {
            setError(`Failed to fetch monthly data: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(prev => ({ ...prev, monthly: false }));
        }
    };

    // Fetch payment list for a specific year and month
    const fetchPaymentList = async (year, month) => {
        try {
            setLoading(prev => ({ ...prev, list: true }));
            setError('');
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/sfrtpayments/analytics/list/${year}/${month}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            if (response.data.success) {
                setPaymentList(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching payment list:', err);
            setError(`Failed to fetch payment list: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(prev => ({ ...prev, list: false }));
        }
    };

    useEffect(() => {
        fetchYearlyData();
    }, []);

    const handleYearClick = async (year) => {
        setSelectedYear(year);
        setView('monthly');
        await fetchMonthlyData(year);
    };

    const handleMonthClick = async (year, month) => {
        // Convert month name back to number (1-12)
        const monthNames = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];
        const monthNumber = monthNames.indexOf(month) + 1;
        
        setSelectedMonth(month);
        setView('list');
        await fetchPaymentList(year, monthNumber);
    };

    const handleBack = () => {
        if (view === 'list') {
            setView('monthly');
            setSelectedMonth(null);
        } else if (view === 'monthly') {
            setView('yearly');
            setSelectedYear(null);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-SG', {
            style: 'currency',
            currency: 'SGD'
        }).format(value);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-SG');
    };

    const getCategoryColor = (category) => {
        switch (category) {
            case 'SFT':
                return 'bg-orange-100 text-orange-800';
            case 'SRT':
                return 'bg-pink-100 text-pink-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getCurrencyColor = (currency) => {
        switch (currency) {
            case 'USDT':
                return 'bg-green-100 text-green-800';
            case 'SGD':
                return 'bg-blue-100 text-blue-800';
            case 'LTC':
                return 'bg-purple-100 text-purple-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const renderYearlyChart = () => (
        <ResponsiveContainer width="100%" height={400}>
            <BarChart 
                data={yearlyData}
                margin={{ top: 40, right: 30, left: 20, bottom: 5 }}
            >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" />
                <YAxis 
                    tickFormatter={(value) => formatCurrency(value)}
                    width={100}
                    domain={[0, 'auto']}
                />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Bar
                    dataKey="totalAmount"
                    fill="#8884d8"
                    name="Total Payments (SGD)"
                    onClick={(data) => handleYearClick(data.year)}
                    cursor="pointer"
                >
                    <LabelList
                        dataKey="totalAmount"
                        position="top"
                        formatter={(value) => formatCurrency(value)}
                        style={{ fill: '#666' }}
                    />
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );

    const renderMonthlyChart = () => (
        <ResponsiveContainer width="100%" height={400}>
            <BarChart 
                data={monthlyData}
                margin={{ top: 40, right: 30, left: 20, bottom: 5 }}
            >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis 
                    tickFormatter={(value) => formatCurrency(value)}
                    width={100}
                    domain={[0, 'auto']}
                />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Bar
                    dataKey="totalAmount"
                    fill="#82ca9d"
                    name="Monthly Payments (SGD)"
                    onClick={(data) => handleMonthClick(selectedYear, data.month)}
                    cursor="pointer"
                >
                    <LabelList
                        dataKey="totalAmount"
                        position="top"
                        formatter={(value) => formatCurrency(value)}
                        style={{ fill: '#666' }}
                    />
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );

    const renderPaymentList = () => (
        <div className="mt-4">
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Payment Date
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                SFRT ID
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Category
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Currency
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Amount (SGD)
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Remarks
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {paymentList.length > 0 ? (
                            paymentList.map((payment) => (
                                <tr key={payment._id}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {formatDate(payment.paymentDate)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap font-medium">
                                        {payment.sfrtID || 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getCategoryColor(payment.category)}`}>
                                            {payment.category || 'N/A'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getCurrencyColor(payment.currency)}`}>
                                            {payment.currency || 'N/A'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap font-medium text-green-600">
                                        {formatCurrency(payment.amountSGD)}
                                    </td>
                                    <td className="px-6 py-4">
                                        {payment.remarks || '-'}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                                    No payments found for this period
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );

    const renderLoading = () => (
        <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
    );

    // Calculate total amount across all years
    const totalAmount = yearlyData.reduce((sum, year) => sum + (year.totalAmount || 0), 0);

    return (
        <div className="bg-white rounded-lg shadow w-full">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold px-8 py-8">
                    SFRT Payment Analytics <span style={{ color: 'red' }}>({formatCurrency(totalAmount)})</span>
                </h2>
                {view !== 'yearly' && (
                    <button
                        onClick={handleBack}
                        className="flex items-center text-blue-600 hover:text-blue-800 mr-8"
                    >
                        <FiArrowLeft className="mr-2" />
                        Back
                    </button>
                )}
            </div>

            {error && (
                <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md mx-8">
                    {error}
                </div>
            )}

            {view === 'yearly' && (loading.yearly ? renderLoading() : renderYearlyChart())}
            {view === 'monthly' && (
                <>
                    <h3 className="text-lg font-medium mb-4 px-8">
                        Monthly Payments for {selectedYear}{' '}
                        <span className="text-red-600">
                            ({formatCurrency(monthlyData.reduce((sum, month) => sum + (month.totalAmount || 0), 0))})
                        </span>
                    </h3>
                    {loading.monthly ? renderLoading() : renderMonthlyChart()}
                </>
            )}
            {view === 'list' && (
                <>
                    <h3 className="text-lg font-medium mb-4 px-8">
                        Payments for {selectedMonth} {selectedYear}{' '}
                        <span className="text-red-600">
                            ({formatCurrency(paymentList.reduce((sum, payment) => sum + (payment.amountSGD || 0), 0))})
                        </span>
                    </h3>
                    {loading.list ? renderLoading() : renderPaymentList()}
                </>
            )}
        </div>
    );
};

export default SFRTPaymentAnalytics;
